import {HttpRequest,HttpMethod,axiosInstance} from "./http";

class ShopeeProvider extends HttpRequest {
	

	RefreshToken(params) {
        return this.request(HttpMethod.Get,`/api/platform/shopee/refreshtoken`);
    }

	// Tiktok Sync Porduct
	SyncProduct(params) {
		return this.request(HttpMethod.Post,`/api/platform/shopee/products/sync`, params);
	}

	SyncOrder(params) {
		return this.request(HttpMethod.Post,`/api/platform/shopee/orders/sync`, params);
	}

	ConnectPlatform(params) {
		return this.request(HttpMethod.Post,`/api/platform/shopee/connect`, params);
	}

	// #260 — push one Shopee listing into the seller catalogue (mdfpf_product).
	// `params` is built by src/utils/shopeeToSeller.js#toSellerBody:
	// {platform_shop_id, category_id?, dry_run}. dry_run:true returns the
	// preview and writes nothing on the seller side; dry_run:false performs the
	// upsert. See docs/specs/2026-09-22-shopee-master-product-sync-design.md §6A.
	ToSeller(itemId, params) {
		return this.request(HttpMethod.Post, `/api/platform/v2/shopee/products/${itemId}/to-seller`, params);
	}

	// #260 — badge lookup for a loaded page of /admin/product_platform.
	// `params` is {platform_shop_id, item_ids} where item_ids is a
	// comma-separated list of at most 200 ids (SELLER_LINKS_MAX_IDS); callers
	// build the batches with buildSellerLinkRequests.
	SellerLinks(params) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/products/seller-links`, params);
	}

	// ---- #280 Phase 5: sync the WHOLE shop into seller ---------------------
	// Spec docs/specs/2026-09-22-shopee-master-product-sync-design.md §6C.3.
	// Bodies/queries are built by src/utils/shopeeBulkSync.js so the rules
	// (platform_shop_id as a string, category_id always present, item_status
	// defaults, page bounds) are asserted by unit tests rather than trusted here.

	// POST — start a job. `params` = bulkStartBody(...). 202 {job_id, status}.
	// 409 {code:"bulk_in_flight", job_id} when a job of this shop is still
	// running: the caller attaches to that job (bulkInFlightJobId) instead of
	// treating it as an error.
	BulkStart(body) {
		return this.request(HttpMethod.Post, `/api/platform/v2/shopee/bulk-sync`, body);
	}

	// GET — "งานล่าสุด": past jobs of one shop, newest first.
	// `params` = {platform_shop_id, limit} (limit <= 50, default 10).
	BulkList(params) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/bulk-sync`, params);
	}

	// GET — one job row; polled every BULK_POLL_INTERVAL_MS while in flight.
	BulkGet(id) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/bulk-sync/${id}`);
	}

	// GET — per-item results. `params` = bulkItemsParams(status, page, pageSize)
	// = {status?, page, page_size}; answers {total, items} ordered by seq.
	BulkItems(id, params) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/bulk-sync/${id}/items`, params);
	}

	// POST — request cancellation. Answers the job row, or 409
	// {code:"bulk_finished"} when it already ended.
	BulkCancel(id) {
		return this.request(HttpMethod.Post, `/api/platform/v2/shopee/bulk-sync/${id}/cancel`);
	}

	// GET — #286/#646: the whole job as one .xlsx of three sheets
	// (สรุป / รายการ / SKU ที่ต้องแก้บน Shopee). `params` = bulkExportParams(tab)
	// = {status?}; an omitted status exports every row.
	//
	// 200 application/vnd.openxmlformats-officedocument.spreadsheetml.sheet with
	// `Content-Disposition: attachment; filename="…"; filename*=UTF-8''…` — the
	// caller saves it under bulkExportFilename(resp.headers, id).
	// 404 {code:"bulk_not_found"} · 409 {code:"bulk_in_flight"} while the job is
	// still queued / listing / running, which is why the button is disabled
	// unless canExportBulk(job).
	//
	// NOT this.request(): HttpRequest.request takes (method, url, payload) and
	// has nowhere to put an axios config, so a responseType passed to it is
	// silently dropped and the .xlsx comes back as a mangled UTF-8 string. Every
	// other binary download in this app goes straight to axiosInstance for the
	// same reason (see src/resource/modernTrade.js).
	BulkExport(id, params) {
		return axiosInstance.get(`/api/platform/v2/shopee/bulk-sync/${id}/export`, {
			params: params,
			responseType: "blob"
		});
	}

	// ---- #284: the shop's OWN name on Shopee --------------------------------
	// GET /api/platform/v2/shopee/shops/:shop_id/info — 200 {shop_id, shop_name,
	// region, status, fetched_at} · 400 {code:"shop_required"} · 502
	// {code:"shop_info_failed", message}. The BE caches it for an hour, so the
	// modals may ask on every open.
	//
	// DECORATION, NOT DATA THE SCREEN DEPENDS ON: the header shows "-" when this
	// fails and the modal keeps working. Callers must never await it before
	// rendering (see ShopeeBulkSyncModal.loadShopInfo).
	ShopInfo(shopId) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/shops/${shopId}/info`);
	}

	// ---- #282: Shopee global category -> seller category map ---------------
	// Spec docs/specs/2026-09-22-shopee-master-product-sync-design.md §6D.4.
	// The map is what lets a sync run WITHOUT the operator picking a category:
	// an unmapped Shopee category auto-creates a seller category of the same
	// name, and this screen is where that guess is corrected.

	// GET — the map page's table. `params` = {platform_shop_id}. Answers
	// {fetched_at, rows:[…]}; rows are normalised by categoryMapRows.
	CategoryMapList(params) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/category-map`, params);
	}

	// PUT — save the operator's edits. `body` = {items:[…]} built by
	// categoryMapDiff, i.e. ONLY the changed rows (category_id 0 deletes the
	// row). 200 {updated:n} · 400 {code:"bad_category"} for an id that does not
	// exist or was soft-deleted.
	CategoryMapPut(body) {
		return this.request(HttpMethod.Put, `/api/platform/v2/shopee/category-map`, body);
	}

	// POST — re-read Shopee's category tree into the cache. `body` =
	// {platform_shop_id}. 200 {count, fetched_at} · 502 when get_category fails.
	CategoryMapRefresh(body) {
		return this.request(HttpMethod.Post, `/api/platform/v2/shopee/category-map/refresh`, body);
	}

	// GET — search the cached Shopee tree by name, so a category the shop does
	// not use yet can still be mapped ahead of time. `params` =
	// {platform_shop_id, q}; answers {rows:[…]} capped at 50.
	CategorySearch(params) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/categories`, params);
	}

	// ---- B7: Shopee promotions -> seller (Discount + Shop Flash Sale) -------
	// Spec docs/specs/2026-10-02-shopee-promotions-to-seller-design.md §5.1.
	// Queries/bodies are built by src/utils/shopeePromotion.js so the rules
	// (platform_shop_id as a STRING — #580, dry_run defaulting to TRUE, an
	// OMITTED `promotions` meaning "the whole shop") are asserted by unit tests
	// rather than trusted here.

	// GET — the page's table: the shop's promotions plus what we did about each.
	// `params` = listParams(shopId, {type, status, page, pageSize}).
	// 200 {shop_id, fetched_at, mode, promotions:[…], more, warnings,
	//      show_upcoming} · 400 {code:"shop_required"} · 404 shop_not_found
	// · 422 bad_promotion_type · 502 {code:"promotion_fetch_failed", message}
	// · 503 {code:"promotion_sync_disabled"}.
	PromotionList(params) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/promotions`, params);
	}

	// POST — run the mirror once, SYNCHRONOUSLY (§5.1: two list calls plus two
	// per promotion, ~20 s for a 20-promotion shop — there is no job row to
	// poll, unlike BulkStart). `body` = buildSyncBody({shopId, dryRun, selected}).
	// 200 {dry_run, run_id, promotions_total, promotions_applied, skus_set,
	//      skus_cleared, skus_skipped, results:[…], warnings}
	// 409 {code:"promotion_sync_in_flight", run_id} when a run of this shop is
	// still going: the caller attaches to that run (promotionInFlightRunId)
	// instead of treating it as an error · 422 {code:"shop_not_master"} for any
	// shop but the master one (owner decision §10.5).
	PromotionSync(body) {
		return this.request(HttpMethod.Post, `/api/platform/v2/shopee/promotions/sync`, body);
	}

	// GET — "รันล่าสุด" and the 3-second poll target while a run is in flight.
	// `params` = runsParams(shopId, limit) = {platform_shop_id, limit} (<= 50,
	// default 10). Reads mdfpf_platform_promotion_run only — NO Shopee call — so
	// polling it costs the shop no quota.
	//
	// 200 EVEN WHEN THE FEATURE IS OFF, carrying {enabled:false}: that is how the
	// page tells "ปิดอยู่" apart from a failure, so this must never be treated as
	// an error path.
	PromotionRuns(params) {
		return this.request(HttpMethod.Get, `/api/platform/v2/shopee/promotions/runs`, params);
	}

	// ---- Shopee cancel reconcile (S1/S2) -----------------------------------
	// Spec docs/specs/2026-10-07-shopee-cancel-reconcile-design.md §3, §4, §11.
	// Both are mw.IsAdmin. Params/bodies are built by
	// src/utils/shopeeCancelCensus.js (censusParams / applyBody).

	// GET — asks Shopee about every Shopee order of the window that BO has as
	// 140 or IN_CANCEL. `params` = {time_from, time_to, shop_id?} (≤ 35 days,
	// YYYY-MM-DD). Slow: ~15 s for 7 days (get_order_detail in 50s). Writes
	// nothing. 200 {time_from, time_to, total, summary, orders} · 400 window.
	CancelCensus(params) {
		return this.request(HttpMethod.Get, `/api/v1/platform/shopee/cancel-census`, params);
	}

	// GET ?format=csv — the same census as a UTF-8 CSV (with BOM). Through
	// axiosInstance, not window.open: the API authenticates with the Bearer
	// header only (no token in the query), and this.request() cannot carry
	// responseType (see BulkExport above). The caller saves the blob.
	CancelCensusCsv(params) {
		return axiosInstance.get(`/api/v1/platform/shopee/cancel-census`, {
			params: Object.assign({}, params, { format: "csv" }),
			responseType: "blob"
		});
	}

	// POST — repair. `payload` = {bo_ids, dry_run}. Shopee is asked again for
	// every id (also on dry_run); only stale_cancel_live / missing_cancel are
	// touched. 200 {dry_run, applied:[…], skipped:[{bo_id, reason}]} · 400 for
	// an empty list or more than 1000 ids.
	CancelCensusApply(payload) {
		return this.request(HttpMethod.Post, `/api/v1/platform/shopee/cancel-census/apply`, payload);
	}

}

export default ShopeeProvider
